import {
  Avatar,
  Badge,
  Button,
  Checkbox,
  Popover,
  Space,
  TableColumnType,
  Tooltip,
} from 'antd'
import moment from 'moment'
import React, { ReactElement, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { ColDef } from 'ag-grid-community'
import styles from './dentureLedgerPage.module.css'
import { decrypt } from '../../../../models/m'
import { DateTimeFormat, DateTimeFormatSimpleOnly } from '../../../../models/datetime'
import { getSexData } from '../../../../models/ToothHome'
import { ToothComponents } from '../../../treatment/editor/modals/toothComponents'
import { getDentureLedgerStatus } from '../../../../models/dentureLedgerPage'
import { getMaskPhone } from '../../../../models/phone'

type Action =
  | 'edit'
  | 'delete'

export const DoctorAuditEntrieAGColumns = (
  startIndex?: any,
  onAction?: (
    action: Action,
    t: any,
  ) => void,
  columnList?: any,
  SYCKHRSJHData?: boolean,
): ColDef[] => {

  return (
    columnList?.map((item: any) => {
      // 根据注释掉的列配置，补充 cellRendererFramework 或 valueFormatter
      switch (item.field) {
        case 'index':
          return {
            ...item,
            cellStyle: { textAlign: 'center', lineHeight: '70px' },
            cellRendererFramework: function IndexCellRenderer(params: any) {
              if (params.node.rowPinned) {
                return ''
              }
              return `${Number(startIndex || 0) + params.node.rowIndex + 1}`
            },
          };
        case 'sex':
          return {
            ...item,
            cellStyle: { textAlign: 'center', lineHeight: '70px' },
            cellRendererFramework: function SexCellRenderer(params: any) {
              const { value } = params;
              if (params.node.rowPinned) return '';
              return getSexData(Number(value));
            },
          };
        case 'patientName':
          return {
            ...item,
            cellStyle: { textAlign: 'center', lineHeight: '70px' },
            cellRendererFramework: function PatientNameCellRenderer(params: any) {
              const t = params.data;
              // 处理 pinned row（合计行）
              if (params.node.rowPinned) {
                return ''
              }
              return t?.patientName ? t?.patientName : '-'
            }
          };
        case 'ageYear':
        case 'variety':
        case 'productionUnit':
        case 'registrationCertificateNo':
        case 'customOrderNo':
        case 'doctorName':
          return {
            ...item,
            cellStyle: (params: any) => {
              const t = params.data;
              return {
                textAlign: 'center',
                lineHeight: '70px'
              };
            },
            cellRendererFramework: function CommonCellRenderer(params: any) {
              if (params.node.rowPinned) return '';
              return params.value || '-';
            },
          };
        case 'toothPosition':
          return {
            ...item,
            cellStyle: { textAlign: 'center', minHeight: '68px', display: 'flex', alignItems: 'center' },
            cellRendererFramework: function ToothPositionCellRenderer(params: any) {
              if (params.node.rowPinned) return '';
              const t = params.data;
              return (
                <ToothComponents
                  key={t?.toothPosition}
                  isEdit={false}
                  data={t?.toothPosition || '--@--@--@--'}
                />
              );
            }
          };
        case 'phoneNumber':
          return {
            ...item,
            cellStyle: { textAlign: 'center', lineHeight: '70px' },
            cellRendererFramework: function PhoneNumberCellRenderer(params: any) {
              if (params.node.rowPinned) return '';
              return params.value ? (!SYCKHRSJHData ? getMaskPhone(decrypt(params.value)) : decrypt(params.value)) : '-';
            },
          };
        case 'customDate':
          return {
            ...item,
            cellStyle: { textAlign: 'center', lineHeight: '70px' },
            cellRendererFramework: function DateCellRenderer(params: any) {
              if (params.node.rowPinned) return '';
              return params.value && moment(params.value).isValid() ? moment(params.value)?.format(DateTimeFormatSimpleOnly) : '-';
            },
          };
        case 'action':
          return {
            ...item,
            cellStyle: { textAlign: 'center', lineHeight: '70px' },
            cellRendererFramework: function ActionCellRenderer(params: any) {
              if (params.node.rowPinned) return '';
              const t = params.data;
              // 确保只在合适的状态下显示退费按钮，避免重复显示
              return (
                <>
                  <Button
                    type='link'
                    size='small'
                    onClick={() => onAction && onAction('edit', t)}
                  >
                    编辑
                  </Button>
                  <Button
                    danger
                    size='small'
                    type='link'
                    onClick={() => onAction && onAction('delete', t)}
                  >
                    删除
                  </Button>
                </>
              );
              return '-';
            }
          };
        default:
          return {
            ...item,
            cellStyle: { textAlign: 'center', lineHeight: '70px' },
          };
      }
    })
  )
}

export const WJGColumns = (
  startIndex: number,
  onAction: (action: string, t: any) => void
): TableColumnType<any>[] => {
  return [
    //收费表格
    {
      title: '序号',
      key: 'no',
      align: 'center',
      width: '3.5rem',
      ellipsis: true,
      render: (_1, _2, index) => `${startIndex + index + 1}`,
    },
    {
      title: '患者姓名',
      dataIndex: 'patientName',
      align: 'center',
      width: '7rem',
      ellipsis: true,
      render: (_, t) => t?.patientName ? t?.patientName : '-',
    },
    {
      title: '性别',
      dataIndex: 'sex',
      align: 'center',
      width: '5rem',
      ellipsis: true,
      render: (_, t) => getSexData(Number(t.sex)),
    },
    {
      title: '年龄',
      dataIndex: 'ageYear',
      align: 'center',
      width: '4.5rem',
      ellipsis: true,
      render: (_, r) => r?.ageYear || '-',
    },
    {
      title: '加工内容',
      dataIndex: 'variety',
      align: 'center',
      width: '7rem',
      ellipsis: true,
      render: (_, t) => t?.variety ? t?.variety : '-',
    },
    {
      title: '色号',
      dataIndex: 'processItemColorNo',
      align: 'center',
      width: '6rem',
      ellipsis: true,
      render: (_, t) => t?.processItemColorNo ? t?.processItemColorNo : '-',
    },
    {
      title: '加工单位',
      dataIndex: 'productionUnit',
      align: 'center',
      width: '7rem',
      ellipsis: true,
      render: (_, t) => t?.productionUnit ? t?.productionUnit : '-',
    },
    {
      title: '加工单号',
      dataIndex: 'customOrderNo',
      align: 'center',
      width: '9rem',
      ellipsis: true,
      render: (_, t) => t?.customOrderNo ? t?.customOrderNo : '-',
    },
    {
      title: '送件日期',
      dataIndex: 'customDate',
      align: 'center',
      width: '8rem',
      ellipsis: true,
      render: (_, t) => t?.customDate ? moment(t?.customDate)?.format(DateTimeFormatSimpleOnly) : '-',
    },
    {
      title: '医生',
      dataIndex: 'doctorName	',
      align: 'center',
      width: '6rem',
      ellipsis: true,
      render: (_, t) => t?.doctorName ? t?.doctorName : '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      align: 'center',
      width: '6rem',
      ellipsis: true,
      render: (_, t) => getDentureLedgerStatus(Number(t.status)),
    },
    {
      title: '操作',
      key: 'action',
      align: 'center',
      width: '6rem',
      render: function Element(_, t) {
        return (
          <Space className={styles.buttonStyle}>
            <Button type='primary' ghost size='small' onClick={() => onAction('relevancy', t)}>
              关联
            </Button>
          </Space>
        )
      },
    },
  ]
}

